/**
 * assets/spatialx-card-actions.js
 * Global, load-once behaviour for snippets/spatialx-card.liquid's two
 * inline transport controls (PLAY, BAG) and the persistent bottom Now
 * Playing bar (snippets/spatialx-now-playing-bar.liquid).
 *
 * One shared <audio> singleton means only one card previews at a time
 * across the whole page (collection grid, home strip, related tracks,
 * search results, artist discography — all render the same card snippet).
 * The Now Playing bar is a page-wide singleton driven by the same audio
 * element: starting a preview opens it, switching tracks updates it,
 * pausing/resuming just toggles its icon (it stays open across a pause —
 * only its own close button, or the track ending, actually hides it).
 *
 * Both card buttons live inside the card's outer <a>; preventDefault() +
 * stopPropagation() on their click run before the anchor's navigation is
 * evaluated, so the click never falls through to the PDP.
 */
(function () {
  'use strict';

  var audio = null;
  var activeBtn = null;
  var playbackRequest = 0;

  var np = null; // memoized Now Playing bar element refs, see getNowPlayingEls()

  function formatTime(seconds) {
    if (!isFinite(seconds) || seconds < 0) seconds = 0;
    var m = Math.floor(seconds / 60);
    var s = Math.floor(seconds % 60);
    return m + ':' + (s < 10 ? '0' : '') + s;
  }

  function getNowPlayingEls() {
    if (np) return np;
    var bar = document.getElementById('sx-now-playing');
    if (!bar) return null;

    np = {
      bar: bar,
      fill: bar.querySelector('[data-sx-np-fill]'),
      art: bar.querySelector('[data-sx-np-art]'),
      title: bar.querySelector('[data-sx-np-title]'),
      artist: bar.querySelector('[data-sx-np-artist]'),
      time: bar.querySelector('[data-sx-np-time]'),
      toggle: bar.querySelector('[data-sx-np-toggle]'),
      close: bar.querySelector('[data-sx-np-close]'),
      scrub: bar.querySelector('[data-sx-np-scrub]'),
      links: bar.querySelectorAll('[data-sx-np-link]'),
    };

    np.toggle.addEventListener('click', function () {
      if (activeBtn) togglePlay(activeBtn);
    });
    np.close.addEventListener('click', closeNowPlaying);
    np.scrub.addEventListener('click', function (e) {
      var a = getAudio();
      if (!a.duration || !isFinite(a.duration)) return;
      var rect = np.scrub.getBoundingClientRect();
      var ratio = (e.clientX - rect.left) / rect.width;
      ratio = Math.max(0, Math.min(1, ratio));
      a.currentTime = ratio * a.duration;
    });

    return np;
  }

  function getAudio() {
    if (audio) return audio;
    audio = new Audio();
    audio.preload = 'none';

    audio.addEventListener('play', function () {
      syncPlayingState(true);
      // Unified site-wide exclusivity contract: every audio surface (radio
      // bar, card previews, the PDP downconverter) dispatches
      // 'sx:audio-play' with its source when it starts, and pauses itself
      // on anyone else's — only one broadcast at a time.
      try { window.dispatchEvent(new CustomEvent('sx:audio-play', { detail: { source: 'card' } })); } catch (e) {}
    });
    audio.addEventListener('pause', function () {
      // 'pause' also fires immediately before 'ended' — let the 'ended'
      // handler own that transition (full close) instead of double-firing.
      if (!audio.ended) syncPlayingState(false);
    });
    audio.addEventListener('ended', closeNowPlaying);
    audio.addEventListener('timeupdate', function () {
      if (!audio.duration || !isFinite(audio.duration)) return;
      var ratio = audio.currentTime / audio.duration;

      if (activeBtn) activeBtn.style.setProperty('--sx-card-progress', ratio);

      var els = getNowPlayingEls();
      if (els) {
        els.fill.style.width = (ratio * 100) + '%';
        els.time.textContent = formatTime(audio.currentTime) + ' / ' + formatTime(audio.duration);
      }
    });

    return audio;
  }

  // Reflects actual audio.play()/pause() state onto both the active card
  // and the Now Playing bar. Does NOT open or close the bar — that's
  // openNowPlaying()/closeNowPlaying()'s job — so pausing mid-track leaves
  // the bar visible (paused), matching how a real player behaves.
  function syncPlayingState(isPlaying) {
    if (activeBtn) {
      var card = activeBtn.closest('.sx-card');
      activeBtn.classList.toggle('is-playing', isPlaying);
      if (activeBtn.hasAttribute('data-seam-track')) activeBtn.setAttribute('aria-pressed', String(isPlaying));
      if (card) card.classList.toggle('sx-card--playing', isPlaying);
    }
    var els = getNowPlayingEls();
    if (els) els.bar.classList.toggle('sx-now-playing--playing', isPlaying);
  }

  function openNowPlaying(btn) {
    var els = getNowPlayingEls();
    if (!els) return;

    var cover = btn.getAttribute('data-cover');
    els.art.style.backgroundImage = cover ? 'url(' + cover + ')' : 'none';
    els.title.textContent = btn.getAttribute('data-title') || '';
    els.artist.textContent = btn.getAttribute('data-artist') || '';

    var url = btn.getAttribute('data-url') || '#';
    for (var i = 0; i < els.links.length; i++) {
      els.links[i].setAttribute('href', url);
    }

    els.fill.style.width = '0%';
    els.time.textContent = '0:00 / 0:00';

    els.bar.classList.add('sx-now-playing--visible');
    els.bar.removeAttribute('inert');
    els.bar.setAttribute('aria-hidden', 'false');
  }

  function closeNowPlaying() {
    playbackRequest += 1;
    var a = getAudio();
    a.pause();

    if (activeBtn) {
      var card = activeBtn.closest('.sx-card');
      activeBtn.classList.remove('is-playing');
      if (activeBtn.hasAttribute('data-seam-track')) activeBtn.setAttribute('aria-pressed', 'false');
      activeBtn.style.setProperty('--sx-card-progress', 0);
      if (card) card.classList.remove('sx-card--playing');
    }
    activeBtn = null;

    var els = getNowPlayingEls();
    if (els) {
      els.bar.classList.remove('sx-now-playing--visible', 'sx-now-playing--playing');
      els.bar.setAttribute('aria-hidden', 'true');
      els.bar.setAttribute('inert', '');
    }
  }

  function playCurrentAudio(a) {
    var request = ++playbackRequest;
    var promise = a.play();
    if (promise && typeof promise.catch === 'function') {
      promise.catch(function () {
        if (request === playbackRequest) closeNowPlaying();
      });
    }
  }

  function togglePlay(btn) {
    var url = btn.getAttribute('data-preview-url');
    if (!url) return;

    var resolvedUrl = url;
    try { resolvedUrl = new URL(url, document.baseURI).href; } catch (_urlError) {}
    if (audio && activeBtn === btn && (audio.currentSrc || audio.src) === resolvedUrl && !audio.paused) {
      playbackRequest += 1;
      audio.pause();
      return;
    }
    var a = getAudio();
    var listeningPass = window.SpatialxListeningPass;
    var watchPlayback = function () {
      if (listeningPass && listeningPass.watchMedia) listeningPass.watchMedia(a, {
        source: 'card',
        trigger: document.activeElement,
        resume: function () { if (btn.isConnected || activeBtn === btn) togglePlay(btn); }
      });
    };

    // A dynamic feature can reuse one button while changing its release.
    // Treat it as the same track only when both the button and audio URL
    // still match; otherwise the new cover must load its own preview.
    if (activeBtn === btn && (a.currentSrc || a.src) === resolvedUrl) {
      if (a.paused) {
        watchPlayback();
        playCurrentAudio(a);
      } else {
        playbackRequest += 1;
        a.pause();
      }
      return;
    }

    // Switching to a different track (or starting the first one). Clear
    // the outgoing card's playing state explicitly and synchronously —
    // changing audio.src doesn't reliably fire a 'pause' event across
    // browsers, so we can't rely on the audio element's own events to
    // clean up the previous card once activeBtn has already moved on to
    // the new one (that was the bug: switching tracks left the old card
    // stuck showing a pause icon and red glow forever).
    playbackRequest += 1;
    if (activeBtn) {
      var prevCard = activeBtn.closest('.sx-card');
      activeBtn.classList.remove('is-playing');
      if (activeBtn.hasAttribute('data-seam-track')) activeBtn.setAttribute('aria-pressed', 'false');
      activeBtn.style.setProperty('--sx-card-progress', 0);
      if (prevCard) prevCard.classList.remove('sx-card--playing');
    }

    // Explicitly stop the outgoing track before loading the new one.
    // Reassigning .src on a playing <audio> element is *supposed* to halt
    // the previous resource implicitly, but that's not reliably immediate
    // across every browser/platform — a bare pause() first removes any
    // ambiguity, so the old track never audibly overlaps the new one.
    if (!a.paused) a.pause();

    if (a.src !== url) a.src = url;
    activeBtn = btn;
    openNowPlaying(btn);

    watchPlayback();
    playCurrentAudio(a);
  }

  function addToBag(btn) {
    if (btn.dataset.state === 'pending' || btn.dataset.state === 'added') return;
    var id = btn.getAttribute('data-variant-id');
    if (!id) return;

    var label = btn.querySelector('[data-sx-card-bag-label]') || btn;
    var original = label.textContent;

    var cart = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
    var sectionIds = cart && typeof cart.getSectionsToRender === 'function' && typeof cart.renderContents === 'function'
      ? cart.getSectionsToRender().map(function (section) { return section.id; })
      : [];
    var root = window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : '/';
    var cartUrl = window.routes && window.routes.cart_url ? window.routes.cart_url : root + 'cart';
    var addUrl = window.routes && window.routes.cart_add_url ? window.routes.cart_add_url : root + 'cart/add.js';
    var payload = { items: [{ id: id, quantity: 1 }] };
    if (sectionIds.length) {
      payload.sections = sectionIds;
      payload.sections_url = window.location.pathname;
      if (typeof cart.setActiveElement === 'function') cart.setActiveElement(btn);
    }

    btn.dataset.state = 'pending';
    btn.setAttribute('aria-busy', 'true');
    btn.removeAttribute('aria-invalid');
    label.textContent = '···';

    fetch(addUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
      body: JSON.stringify(payload)
    })
      .then(function (response) {
        return response.json().then(function (data) {
          if (!response.ok || data.status) throw new Error('Cart request failed');
          return data;
        });
      })
      .then(function (data) {
        btn.dataset.state = 'added';
        btn.removeAttribute('aria-busy');
        label.textContent = '✓ BAG';
        var sectionsReady = sectionIds.length && data.sections && sectionIds.every(function (sectionId) {
          return typeof data.sections[sectionId] === 'string' && data.sections[sectionId].trim();
        });
        if (!sectionsReady) {
          window.location.assign(cartUrl);
          return;
        }
        try {
          var addedItem = data.items ? data.items[0] : data;
          if (btn.isConnected === false && typeof cart.setActiveElement === 'function') {
            cart.setActiveElement(document.getElementById('cart-icon-bubble'));
          }
          cart.renderContents(Object.assign({}, addedItem, { sections: data.sections }));
        } catch (error) {
          window.location.assign(cartUrl);
          return;
        }
        setTimeout(function () {
          btn.dataset.state = '';
          label.textContent = original;
        }, 2200);
      })
      .catch(function () {
        btn.dataset.state = '';
        btn.removeAttribute('aria-busy');
        label.textContent = original;
        btn.setAttribute('aria-invalid', 'true');
        setTimeout(function () { btn.removeAttribute('aria-invalid'); }, 2200);
      });
  }

  document.addEventListener('click', function (e) {
    var playBtn = e.target.closest('[data-sx-card-play]');
    if (playBtn) {
      e.preventDefault();
      e.stopPropagation();
      togglePlay(playBtn);
      return;
    }

    var bagBtn = e.target.closest('[data-sx-card-bag]');
    if (bagBtn) {
      e.preventDefault();
      e.stopPropagation();
      addToBag(bagBtn);
    }
  });

  // A card's own link is still keyboard/click-navigable; if a preview is
  // playing and the user actually navigates away via the card link (or the
  // Now Playing bar's own title/disc link), stop the audio rather than let
  // it keep playing under the next page.
  window.addEventListener('pagehide', function () {
    playbackRequest += 1;
    if (audio) audio.pause();
  });

  // Mirror of the 'sx:audio-play' dispatch above: when any other audio
  // surface starts (radio bar, PDP downconverter), pause any card preview
  // that's mid-track.
  window.addEventListener('sx:audio-play', function (e) {
    if (e.detail && e.detail.source === 'card') return;
    playbackRequest += 1;
    if (audio && !audio.paused) audio.pause();
  });
})();
